import {inject, Injectable} from "@angular/core"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {mutateThrowingErrors} from "@app/common/helpers/api/mutate"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {ConfiguraPublishing} from "@cm/material-outputs-publishing"
import {JobState} from "@generated"
import {
    MaterialOutputsPublishingConfiguraStatusGQL,
    MaterialOutputsPublishingConfiguraStatusQuery,
    MaterialOutputsPublishingJobStateGQL,
    MaterialOutputsPublishingJobStateQuery,
    MaterialOutputsPublishingTriggerConfiguraBatchGQL,
    MaterialOutputsPublishingTriggerConfiguraGQL,
} from "@platform/services/material/material-outputs-publishing.generated"
import {Observable, switchMap, takeWhile, timer} from "rxjs"

/** Matches `material-outputs`, so a publish and an export feel the same while they run. */
const JOB_POLL_START_MS = 5000
const JOB_POLL_INTERVAL_MS = 10 * 1000

export type ConfiguraPublishBinding = NonNullable<MaterialOutputsPublishingConfiguraStatusQuery["configuraMaterialDatabaseBindings"][number]>
export type PublishJob = MaterialOutputsPublishingJobStateQuery["job"]

/**
 * What the user can do about this material right now. Deliberately stops short of how it should look:
 * "the last run failed and retrying could work" is a fact about the domain, the icon that says so is
 * not. Keeping the split here is what lets the material list (step 18) reuse this without inheriting
 * the detail view's button.
 */
export type ConfiguraPublishAction =
    /**
     * `generatesExport` means the click regenerates the Configura export first and the publish waits
     * for it — minutes rather than seconds, which is the difference worth telling someone about before
     * they press it.
     */
    | {kind: "publish"; generatesExport: boolean}
    | {kind: "republish"; generatesExport: boolean}
    | {kind: "retry"; generatesExport: boolean}
    | {kind: "blocked"; message: string}
    | {kind: "inFlight"; scheduled: boolean}
    | {kind: "upToDate"}

export type ConfiguraPublishStatus = {
    /** The classifier's verdict, as the backend resolved it. */
    state: ConfiguraPublishing.PublishState.PublishState
    /** Null until the material has been bound to a Configura textile, which the first publish does. */
    binding: ConfiguraPublishBinding | null
    action: ConfiguraPublishAction
}

export const isInFlightJobState = (state: JobState) => state === JobState.Init || state === JobState.Runnable || state === JobState.Running

/**
 * The whole state-to-action mapping, in one exhaustive switch. `noImplicitReturns` plus the declared
 * return type means a seventh `PublishState` variant fails to compile here rather than falling through
 * to a button with no behaviour.
 */
type MaybePublishable = Extract<ConfiguraPublishing.PublishState.PublishState, {type: "notPublished"}>["publishable"]

/**
 * A publish is offered when the outputs are ready, and *also* when the only thing missing is the
 * Configura export: publishing owns that output, so the trigger mutation generates one and has the
 * publish job wait for it rather than refusing. Every other blocked reason needs a person to
 * regenerate something first, and stays blocked here.
 */
function offer(publishable: MaybePublishable, kind: "publish" | "republish" | "retry"): ConfiguraPublishAction {
    if (publishable.isPublishable) {
        return {kind, generatesExport: false}
    }
    if (publishable.resolvableByGeneratingExport) {
        return {kind, generatesExport: true}
    }
    return {kind: "blocked", message: publishable.message}
}

function configuraPublishActionFor(state: ConfiguraPublishing.PublishState.PublishState): ConfiguraPublishAction {
    switch (state.type) {
        case "publishedAndUpToDate":
            return {kind: "upToDate"}
        case "publishingInProgress":
            return {kind: "inFlight", scheduled: false}
        case "publishingScheduled":
            return {kind: "inFlight", scheduled: true}
        case "notPublished":
            return offer(state.publishable, "publish")
        case "publishedButOutdated":
            return offer(state.publishable, "republish")
        case "publishingFailed":
            return offer(state.publishable, "retry")
    }
}

/**
 * Publishing generated material outputs to an external content platform, and reading back where each
 * material stands. Configura's Material Database is the only target today; pCon is expected, which is why
 * this is named for the domain and each target keeps its own methods rather than sharing one behind a
 * `target` argument.
 *
 * Root-provided, so it holds no per-material state: it fetches, parses and interprets, while whatever
 * has to be *stored* for one material — the current status, a polling subscription — belongs to the
 * view that owns a lifecycle.
 *
 * The backend's error messages are the actionable content on the write path, so this translates nothing
 * and swallows nothing: errors propagate to the caller, the only party that can undo its own optimistic
 * state.
 */
@Injectable({providedIn: "root"})
export class MaterialOutputsPublishingService {
    readonly notifications = inject(NotificationsService)

    readonly configuraStatusGql = inject(MaterialOutputsPublishingConfiguraStatusGQL)
    readonly jobStateGql = inject(MaterialOutputsPublishingJobStateGQL)
    readonly triggerConfiguraGql = inject(MaterialOutputsPublishingTriggerConfiguraGQL)
    readonly triggerConfiguraBatchGql = inject(MaterialOutputsPublishingTriggerConfiguraBatchGQL)

    /**
     * Where one material stands with the Configura Material Database. The publish state crosses the API
     * as `Json`, so it is validated here at the boundary rather than trusted: without the parse, a
     * renamed field or a deploy-skewed backend arrives as `undefined` deep inside a template instead
     * of as a loud failure at the edge.
     */
    async getConfiguraPublishStatus(materialId: string): Promise<ConfiguraPublishStatus> {
        const {material, configuraMaterialDatabaseBindings} = await fetchThrowingErrors(this.configuraStatusGql)({materialId})
        const state = ConfiguraPublishing.PublishState.parsePublishState(material.configuraMaterialDatabasePublishState)
        // A find-many filtered to one material, so zero or one row — see the query for why.
        const binding = configuraMaterialDatabaseBindings.filter((row): row is ConfiguraPublishBinding => !!row)[0] ?? null
        return {state, binding, action: configuraPublishActionFor(state)}
    }

    /**
     * Submits a publish job for one material to the Configura **Material Database** — spelled out
     * because Configura ship more than one thing and a second integration would make the bare vendor
     * name ambiguous. Binds the material to its Configura textile first if this is its first publish.
     *
     * Resolves with the binding, whose `latestJobTask` is the job just created.
     */
    async triggerConfiguraMaterialDatabasePublish({materialId, showNotification}: {materialId: string; showNotification: boolean}) {
        const {triggerConfiguraMaterialDatabasePublish: result} = await mutateThrowingErrors(this.triggerConfiguraGql)({input: {materialId}})
        if (showNotification) {
            // A created entry is a fact worth surfacing at the click: it is permanent on the
            // Material Database side, and this notification is the only place a single publish reports it.
            this.notifications.showInfo(
                result.createdTextile ? "Configura publish: Material Database entry created, job submitted" : "Configura publish: Job submitted",
                undefined,
                "Dismiss",
            )
        }
        return result.binding
    }

    /**
     * The batch variant: one mutation for the whole selection, one entry per material in input
     * order, with exactly one of `jobId` / `skippedMessage` / `refusalMessage` set. Skips and
     * per-material refusals arrive as entries rather than errors — the caller decides how to
     * present them. Batch-level refusals (a selection spanning organizations, a missing Material Database
     * profile) still throw.
     */
    async triggerConfiguraMaterialDatabasePublishBatch({materialIds}: {materialIds: string[]}) {
        const {triggerConfiguraMaterialDatabasePublishBatch: entries} = await mutateThrowingErrors(this.triggerConfiguraBatchGql)({input: {materialIds}})
        return entries
    }

    /**
     * Cold: polls one publish job until it reaches a terminal state, emits that last state, and
     * completes. Cold is what makes this safe on a singleton — every subscriber gets its own timer and
     * teardown belongs to whoever subscribed. Target-agnostic on purpose; a publish job is a job.
     */
    watchPublishJobUntilSettled$(jobId: string): Observable<PublishJob> {
        return timer(JOB_POLL_START_MS, JOB_POLL_INTERVAL_MS).pipe(
            switchMap(async () => (await fetchThrowingErrors(this.jobStateGql)({id: jobId})).job),
            takeWhile((job) => isInFlightJobState(job.state), true),
        )
    }
}
